import { Box, Typography, useTheme } from "@mui/material";
import CustomDataFields from "../../components/CustomDataFields/CustomDataFields";
import Grid from "@mui/material/Grid2";
import SimpleSelect from "../../components/Forms/FieldTypes/Select";
import SimpleTextField from "../../components/Forms/FieldTypes/TextField";
import { naturalSort } from "../../utils/naturalSort";
import InventoryIcon from "@mui/icons-material/Inventory";
import DescriptionIcon from "@mui/icons-material/Description";

export default function ProductInformation(props) {
  const theme = useTheme();
  const { palette = {} } = theme;
  const { primary = {}, success = {}, mode } = palette;
  const isDark = mode === "dark";
  const greenColor = success?.main || "#A0C46D";
  const navyColor = primary?.main || "#002843";

  const classes = {
    sectionHeader: {
      display: "flex",
      alignItems: "center",
      gap: "10px",
      marginBottom: "16px",
      marginTop: "8px",
      paddingBottom: "10px",
      borderBottom: `2px solid ${isDark ? greenColor + "30" : navyColor + "15"}`,
    },
    sectionTitle: {
      fontFamily: '"Lato", sans-serif',
      fontWeight: 700,
      fontSize: "1rem",
      color: isDark ? greenColor : navyColor,
      letterSpacing: "-0.01em",
    },
    sectionIcon: {
      width: "32px",
      height: "32px",
      borderRadius: "8px",
      background: isDark 
        ? `linear-gradient(135deg, ${greenColor}20 0%, ${greenColor}10 100%)`
        : `linear-gradient(135deg, ${navyColor}15 0%, ${greenColor}10 100%)`,
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      color: isDark ? greenColor : navyColor,
    },
    inputContainer: {
      marginBottom: "8px",
    },
  };

  const {
    addProductLevelDataElement,
    customDataFields = [],
    description = {},
    name = {},
    onChange,
    onRemove,
    productCategoryOptions,
    productType = {},
    readOnly,
    setAddProductLevelDataElement,
    setState,
    tag = {},
  } = props;
  const categoryOptions = productCategoryOptions();

  return (<>
    {/* Section: Basic Information */}
    <Grid size={12}>
      <Box sx={classes.sectionHeader}>
        <Box sx={classes.sectionIcon}>
          <InventoryIcon sx={{ fontSize: "18px" }} />
        </Box>
        <Typography sx={classes.sectionTitle}>
          Basic Information
        </Typography>
      </Box>
    </Grid>

    {/* Product Name */}
    <Grid size={6} sx={classes.inputContainer}>
      <SimpleTextField
        error={name.error}
        id="name"
        inputProps={{ "data-cypress-id": "products-product-mdl-txt-name"}}
        label="Product Name"
        onChange={(event) => {
          onChange(event);
        }}
        readOnly={readOnly}
        required={true}
        value={name.value}
      />
    </Grid>
    {/* Product Code */}
    <Grid size={6} sx={classes.inputContainer}>
      <SimpleTextField
        error={tag.error}
        id="tag"
        inputProps={{ "data-cypress-id": "products-product-mdl-txt-code"}}
        label="Product Code"
        onChange={(event) => {
          onChange(event);
        }}
        readOnly={readOnly}
        required={true}
        value={tag.value}
      />
    </Grid>
    {/* Product Category */}
    <Grid size={6} sx={classes.inputContainer}>
      <SimpleSelect
        error={productType.error}
        label="Product Category"
        id="productType"
        inputProps={{ "data-cypress-id": "products-product-mdl-select-category"}}
        onChange={(event) => {
          onChange({
            target: { id: "productType", value: event.target.value },
          });
        }}
        readOnly={readOnly}
        required={true}
        value={categoryOptions.length > 0 ? productType.value : ""}
        options={categoryOptions.sort((a, b) =>
          naturalSort(a.label, b.label)
        )}
        variant="outlined"
      />
    </Grid>

    {/* Section: Description */}
    <Grid size={12}>
      <Box sx={{ ...classes.sectionHeader, marginTop: "20px" }}>
        <Box sx={classes.sectionIcon}>
          <DescriptionIcon sx={{ fontSize: "18px" }} />
        </Box>
        <Typography sx={classes.sectionTitle}>
          Product Details
        </Typography>
      </Box>
    </Grid>

    {/* Product Description */}
    <Grid size={12} sx={classes.inputContainer}>
      <SimpleTextField
        id="description"
        inputProps={{ "data-cypress-id": "products-product-mdl-txt-description"}}
        label="Product Description"
        multiline
        onChange={(event) => {
          onChange(event);
        }}
        readOnly={readOnly}
        required={false}
        rows={4}
        value={description.value}
      />
    </Grid>
    {/* Custom Data Fields are handled here */}
    {customDataFields.length > 0 || addProductLevelDataElement ? (
      <CustomDataFields
        addField={addProductLevelDataElement}
        customDataFields={customDataFields}
        onRemove={(element) => {
          onRemove(element);
        }}
        readOnly={readOnly}
        setAddProductLevelDataElement={setAddProductLevelDataElement}
        updateCustomFields={(customDataFields) =>
          setState((prevState) => ({ ...prevState, customDataFields }))
        }
      />
    ) : null}
  </>);
}
